import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";
import { currencyInput } from "@/shared/utils/currencyInput";
import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

interface AutoLoanFormValues {
  lenderName: string;
  balance: string;
  monthlyPayment: string;
  statementFile: File | null;
}

const EMPTY_FORM: AutoLoanFormValues = {
  lenderName: "",
  balance: "",
  monthlyPayment: "",
  statementFile: null,
};

export const AutoLoanForm: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    AutoLoanFormValues,
    { lenderName?: string }
  >(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balanceInput = currencyInput(form.balance, (val) => updateForm({ ...form, balance: val }));
  const monthlyPaymentInput = currencyInput(form.monthlyPayment, (val) =>
    updateForm({ ...form, monthlyPayment: val }),
  );

  const handleSubmit = async () => {
    if (isSubmitting || !estateId) return;

    if (!form.lenderName.trim()) {
      setFieldErrors({ lenderName: "Please enter a lender name" });
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let statementDocId: number | undefined;

      if (form.statementFile) {
        statementDocId = await uploadDocumentWithAuth(
          form.statementFile,
          estateId,
          "auto_loan_statement",
        );
      }

      const vehicleId =
        typeof answers["last_vehicle_id"] === "string"
          ? parseInt(answers["last_vehicle_id"])
          : undefined;

      await addItem({
        estate_id: estateId,
        category: "Secured debts",
        classification: "debt",
        item_type: "Auto loans",
        item_name: form.lenderName.trim(),
        balance: parseFloat(form.balance) || 0,
        metadata: {
          lender_name: form.lenderName.trim() || undefined,
          monthly_payment: parseFloat(form.monthlyPayment) || undefined,
        },
        source_document_id: statementDocId,
        linked_item_id: vehicleId && !isNaN(vehicleId) ? vehicleId : undefined,
      });

      goToScreen("vehicle_estimate", {
        ...answers,
        auto_loan_done: "yes",
      });
    } catch (err) {
      setError("Failed to save auto loan. Please try again.");
      sentryUtils.captureError(err, "AutoLoanForm submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AddItemLayout
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting || isAddingItem}
      error={error}
      onSkip={() => {
        goToScreen("vehicle_estimate", {
          ...answers,
          auto_loan_done: "yes",
        });
      }}
      skipLabel="I'll add the details later"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label required>Lender name</Label>
          <Input
            value={form.lenderName}
            onChange={(e) => updateForm({ ...form, lenderName: e.target.value })}
            placeholder="e.g., Wells Fargo, Chase, Ally"
            className={fieldErrors.lenderName ? "border-error" : ""}
          />
          <FieldError message={fieldErrors.lenderName} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>
              Remaining balance <span className="text-tertiary">(optional)</span>
            </Label>
            <CurrencyInput
              value={balanceInput.displayValue}
              onChange={balanceInput.handleChange}
              placeholder="0.00"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>
              Monthly payment <span className="text-tertiary">(optional)</span>
            </Label>
            <CurrencyInput
              value={monthlyPaymentInput.displayValue}
              onChange={monthlyPaymentInput.handleChange}
              placeholder="0.00"
            />
          </div>
        </div>

        <UploadZone
          label="Loan statement"
          labelSuffix="(optional)"
          file={form.statementFile}
          onFileSelect={(file) => updateForm({ ...form, statementFile: file })}
          onFileRemove={() => updateForm({ ...form, statementFile: null })}
        />
      </div>
    </AddItemLayout>
  );
};
